/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import ContentsService from '../../content-creation/services/contents.services';
import { ContentQueryParams, ContentResponse, Folder, SubCategoryType } from '../../../data/types/content.interface';
import AssetContentTypeService from '../../asset-content-type/services/assetContentType.services';
import TagService from '../../tags/tag.services';
import { TagType } from '../../../data/types/tags.interface';
import ContentMarketingService from '../../content-marketing/services/contentMarketing.services';
import { lowerCase } from 'lodash';

const defaultSortDirection = 'desc';
const defaultSortColumn = 'createdAt';

const usePaginatedContent = (initialParams: Partial<ContentQueryParams> = {}) => {
  const [contentCollections, setContentCollections] = useState<ContentResponse[]>([]);
  const [listFilterParams, setListFilterParams] = useState<ContentQueryParams>({
    perPage: 50,
    page: 1,
    type: initialParams.type,
    sort: defaultSortColumn,
    direction: defaultSortDirection,
    ...initialParams,
  });

  const [folderNames, setFolderNames] = useState<Folder[]>([]);
  const [subFolderNames, setSubFolderNames] = useState<Folder[]>([]);
  const [categories, setCategories] = useState<SubCategoryType[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryType[]>([]);
  const [contentTypes, setContentTypes] = useState<TagType[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const isFetching = useRef(false);

  const getContent = async (type: string = '') => {
    switch (type) {
      case 'article':
      case 'vlog':
      case 'video':
      case 'blog':
      case 'quote':
      case 'post':
      case 'book': {
        return await AssetContentTypeService.getContents(listFilterParams);
      }
      case 'brand':
      case 'event':
      case 'identity':
      case 'location':
      case 'person':
      case 'theme':
      case 'author':
        return await TagService.getContents(type, listFilterParams);
      case '':
        if (initialParams.type) {
          return await ContentsService.getAllContents(listFilterParams);
        }
        break;
      case 'all': {
        return await ContentMarketingService.getContents(listFilterParams);
      }
      default:
        return;
    }
  };

  const getAllContents = async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    try {
      const contents = await getContent(initialParams.contentType);
      const data = contents.rows || contents;
      setContentCollections(data);
      setTotalCount(contents.count);
    } catch (error) {
      console.error('Error fetching content:', error);
    }

    isFetching.current = false;
  };

  const getFolders = async () => {
    const list = await ContentsService.getFolderList(initialParams?.type || 'raw');
    setFolderNames(list.mainFolders);
    if (initialParams?.type === 'raw') {
      setSubFolderNames(list.subFolders.filter((f) => !(lowerCase(f.path_display).includes('prod'))));
    } else {
      setSubFolderNames(list.subFolders);
    }
  }

  const getCategories = async () => {
    const categories = await ContentsService.getCategories();
    setCategories(categories);
  };

  const getSubCategories = async () => {
    const subCategories = await ContentsService.getSubCategories();
    setSubCategories(subCategories);
  };

  const getAllContentTypes = async () => {
    const result = await AssetContentTypeService.get();
    setContentTypes(result);
    return result;
  };

  useEffect(() => {
    getFolders();
    getAllContentTypes();
  }, []);

  useEffect(() => {
    getAllContents();
    getCategories();
    getSubCategories();
  }, [listFilterParams]);

  return {
    contentCollections,
    setContentCollections,
    listFilterParams,
    setListFilterParams,
    totalCount,
    folderNames,
    subFolderNames,
    categories,
    subCategories,
    contentTypes,
  };
};

export default usePaginatedContent;
